import { createLayerComponent } from '@react-leaflet/core'
import L from 'leaflet'
import { getPMTilesUrl } from '@graniot/common/adapters/pmtiles'
import * as protomapsL from 'protomaps-leaflet'
import { FetchSource, PMTiles } from 'pmtiles'

const PMTILES_FETCH_ATTEMPTS = 3
const PMTILES_RETRY_BASE_DELAY_MS = 250
const RETRYABLE_PMTILES_ERROR = /Bad response code: (408|429|500|502|503|504)\b|Failed to fetch|NetworkError|Load failed/i

const wait = (delayMs) => new Promise(resolve => setTimeout(resolve, delayMs))

// PMTiles range reads are idempotent. Retry transient origin/proxy failures while
// preserving aborts and permanent HTTP errors.
class RetryingFetchSource extends FetchSource {
  async getBytes(offset, length, signal, etag) {
    for (let attempt = 1; attempt <= PMTILES_FETCH_ATTEMPTS; attempt += 1) {
      try {
        return await super.getBytes(offset, length, signal, etag)
      } catch (error) {
        const message = error?.message || String(error)
        const retryable = !signal?.aborted && RETRYABLE_PMTILES_ERROR.test(message)

        if (!retryable || attempt === PMTILES_FETCH_ATTEMPTS) {
          throw error
        }

        const delayMs = PMTILES_RETRY_BASE_DELAY_MS * (2 ** (attempt - 1))
        console.warn(
          `[PMTilesLayer] Transient range-read failure; retrying in ${delayMs}ms ` +
          `(attempt ${attempt + 1}/${PMTILES_FETCH_ATTEMPTS}): ${message}`
        )
        await wait(delayMs)
      }
    }

    throw new Error('PMTiles range read exhausted without a result')
  }
}

// Custom layer class to handle async loading
class PMTilesLoaderLayer extends L.Layer {
  constructor(options) {
    super(options)
    this.options = options
    this._layer = null
    this._loading = false
  }

  onAdd(map) {
    this._map = map
    this._loadLayer()
    return this
  }

  onRemove(map) {
    if (this._layer && map.hasLayer(this._layer)) {
      map.removeLayer(this._layer)
    }
    this._layer = null
    this._map = null
    return this
  }

  async _loadLayer() {
    if (this._loading || this._layer) return
    this._loading = true

    const {
      layerName,
      style = {},
      minZoom,
      maxZoom,
      isPublic = false
    } = this.options

    // protomaps-leaflet is now imported directly at the top of the file

    // Default style for protected areas
    const defaultStyle = {
      color: '#4DAF4A',
      fillColor: '#4DAF4A',
      fillOpacity: 0.3,
      weight: 2,
      opacity: 0.8,
      ...style
    }

    try {
      // Use the adapter to fetch the presigned URL
      const data = await getPMTilesUrl(layerName, null, isPublic)
      const pmtilesUrl = data.url

      // Use minZoom and maxZoom from backend if provided, otherwise use props or defaults
      const actualMinZoom = data.min_zoom !== undefined ? data.min_zoom : (minZoom !== undefined ? minZoom : 0)
      const actualMaxZoom = data.max_zoom !== undefined ? data.max_zoom : (maxZoom !== undefined ? maxZoom : 22)

      // Import symbolizers from protomaps-leaflet
      const { PolygonSymbolizer, LineSymbolizer } = protomapsL

      // Detect layer name from PMTiles metadata
      let vectorLayerName = data.vector_layer_name || 'protected_areas'
      const pmtilesArchive = new PMTiles(new RetryingFetchSource(pmtilesUrl))
      try {
        const metadata = await pmtilesArchive.getMetadata()
        if (metadata?.vector_layers?.length > 0) {
          vectorLayerName = metadata.vector_layers[0].id
        }
      } catch (e) {
        console.warn(
          `[PMTilesLayer] Could not read metadata for ${layerName}; ` +
          `using configured vector layer "${vectorLayerName}".`,
          e
        )
      }

      // Create the PMTiles layer with proper symbolizers. maxDataZoom is the
      // archive's native maximum; Leaflet may overzoom it for display.
      this._layer = protomapsL.leafletLayer({
        // Reuse the metadata reader, retry source, and PMTiles directory cache.
        url: pmtilesArchive,
        paintRules: [
          {
            dataLayer: vectorLayerName,
            symbolizer: new PolygonSymbolizer({
              fill: defaultStyle.fillColor,
              opacity: defaultStyle.fillOpacity
            })
          },
          {
            dataLayer: vectorLayerName,
            symbolizer: new LineSymbolizer({
              stroke: defaultStyle.color,
              width: defaultStyle.weight,
              opacity: defaultStyle.opacity
            })
          }
        ],
        maxDataZoom: actualMaxZoom,
        maxZoom: 24,  // Allow display up to z24 (overzoom beyond z12)
        minZoom: actualMinZoom,
        attribution: data.attribution || ''
      })

      // Add the layer to the map if we're still added
      if (this._map) {
        this._map.addLayer(this._layer)
      }

    } catch (err) {
      console.error('[PMTilesLayer] Error creating PMTiles layer:', err)
      console.error('[PMTilesLayer] Error details:', err.message, err.stack)
    } finally {
      this._loading = false
    }
  }
}

// Factory function for the custom layer
L.pmtilesLoaderLayer = function(options) {
  return new PMTilesLoaderLayer(options)
}

/**
 * Create a PMTiles layer instance
 */
function createPMTilesLayer(props, context) {
  const {
    layerName,
    layerId,
    displayName = '',
    style = {},
    minZoom,
    maxZoom,
    visible = true,
    isPublic = false
  } = props

  // Create the custom layer instance
  const instance = L.pmtilesLoaderLayer({
    layerName,
    layerId,
    displayName,
    style,
    minZoom,
    maxZoom,
    isPublic
  })

  return { instance }
}

/**
 * Update the layer when props change
 */
function updatePMTilesLayer(layer, props, prevProps) {
  // Style/data changes are handled by React's key mechanism
}

/**
 * PMTilesLayer component for React Leaflet
 * Renders PMTiles directly from S3 using protomaps-leaflet
 *
 * @param {Object} props
 * @param {string} props.layerName - Name of the layer to fetch from backend (e.g., 'protected-areas')
 * @param {string} props.layerId - Unique identifier for this layer instance
 * @param {string} props.displayName - Display name for the layer
 * @param {Object} props.style - Style configuration for the layer
 * @param {number} props.minZoom - Minimum zoom level (optional, will use backend value or 0)
 * @param {number} props.maxZoom - Maximum zoom level (optional, will use backend value or 22)
 * @param {boolean} props.visible - Whether the layer is visible (default: true)
 */
const PMTilesLayer = createLayerComponent(
  createPMTilesLayer,
  updatePMTilesLayer
)

export default PMTilesLayer
